import * as React from 'react';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { services } from '../pages/Sell/services';
import {
  Button,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  Paper,
  Box,
  IconButton,
  Collapse,
  Tooltip,
  Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import QRIcon from '@mui/icons-material/QrCode2Outlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingDownIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUpRounded';
import ScaleIcon from '@mui/icons-material/ScaleOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStatsRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { BASE_URL } from '../services/fetch';
import { useDialogContext } from './dialog-context';
import { QRCodeDialog } from './qrcode-dialog';
import { useTheme } from '@mui/material/styles';

// ================= کامپوننت سطر کشویی (۲ بخش افقی در کنار هم) =================
const CustomRow = React.forwardRef((props, ref) => {
  const { expandedRowId, ...otherProps } = props;
  const isExpanded = expandedRowId === props.rowId;
  const stats = props.row?.sellVolumeStats;
  const items = props.row?.items || [];
  const isNegative = stats
    ? Number(stats.percentageDifferenceWithESTJT) < 0
    : false;

  return (
    <Box
      ref={ref}
      {...otherProps}
      sx={{
        ...props.style,
        height: 'auto !important',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isExpanded ? '#f8fafc' : 'inherit',
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* سطر اصلی DataGrid */}
      <GridRow
        {...otherProps}
        style={{ width: '100%', position: 'relative' }}
      />

      {/* بخش کشویی: تقسیم شده به ۲ ستون افقی در کنار هم */}
      <Collapse in={isExpanded} timeout={180} unmountOnExit>
        <Box
          sx={{
            width: '100%',
            p: 1.5,
            bgcolor: '#f8fafc',
            borderTop: '1px dashed #cbd5e1',
            borderBottom: '2px solid #059669',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
              alignItems: 'stretch',
            }}
          >
            {/* ================= بخش ۱: آمار و تحلیل مظنه قیمت‌ها (Price Stats) ================= */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {/* هدر بخش آمار */}
              <Box
                display="flex"
                // backgroundColor="red"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                ></Stack>
              </Box>

              {stats ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                  }}
                >
                  {/* قیمت فروش فاکتور */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت هر گرم فروش ما
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#0f172a"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.sellPrice).toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Box>

                  {/* مظنه تابلو ESTJT */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت گرم مرجع
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#0f172a"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.ESTJTPrice).toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Box>

                  {/* قیمت هر مثقال */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#64748b"
                      display="block"
                      fontSize="0.7rem"
                    >
                      قیمت مثقال مرجع
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#334155"
                      fontSize="0.8rem"
                      mt={0.3}
                    >
                      {Number(stats.methghalPrice).toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Box>

                  {/* حجم و اختلاف */}
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="#64748b"
                        display="block"
                        fontSize="0.7rem"
                      >
                        اختلاف با مرجع
                      </Typography>
                      <Chip
                        size="small"
                        icon={
                          isNegative ? (
                            <TrendingDownIcon
                              sx={{ '&&': { color: '#dc2626' }, fontSize: 13 }}
                            />
                          ) : (
                            <TrendingUpIcon
                              sx={{ '&&': { color: '#16a34a' }, fontSize: 13 }}
                            />
                          )
                        }
                        label={`${stats.percentageDifferenceWithESTJT}٪`}
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          borderRadius: '4px',
                          bgcolor: isNegative ? '#fef2f2' : '#f0fdf4',
                          color: isNegative ? '#991b1b' : '#166534',
                          border: `1px solid ${isNegative ? '#fecaca' : '#bbf7d0'}`,
                          px: 0.3,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box py={2} textAlign="center">
                  <Typography variant="caption" color="#94a3b8">
                    اطلاعات مظنه برای این فاکتور ثبت نشده است.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* ================= بخش ۲: تفکیک شمش‌ها (هر شمش در یک باکس زیبا) ================= */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* شبکه باکس‌های شمش */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: items.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                  },
                  gap: 1,
                  maxHeight: 130,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {items
                  .slice()
                  .sort(
                    (a, b) =>
                      Number(a.type.substring(7)) - Number(b.type.substring(7))
                  )
                  .map(item => {
                    const weight = Number(item.type.replace('GB_999_', ''));
                    const unitPrice = Number(item.price.value);
                    const lineTotal = item.count * unitPrice;

                    return (
                      <Paper
                        key={item.type}
                        elevation={0}
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          border: '1px solid #e2e8f0',
                          bgcolor: '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: '#ffffff',
                            borderColor: '#cbd5e1',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        {/* تیتر باکس شمش: نوع و تعداد */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="#0f172a"
                            fontSize="0.76rem"
                          >
                            شمش {Number(weight).toLocaleString('fa-IR')} گرمی
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              bgcolor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              color: '#334155',
                              fontWeight: 700,
                              px: 0.8,
                              py: 0.1,
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                            }}
                          >
                            {Number(item.count).toLocaleString('fa-IR')} عدد
                          </Typography>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed', my: 0.2 }} />

                        {/* قیمت‌ها: فی و مجموع */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            color="#64748b"
                            fontSize="0.7rem"
                          >
                            فی: {unitPrice.toLocaleString('fa-IR')} ریال
                          </Typography>
                          {item.count > 1 && (
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="#0f172a"
                              fontSize="0.72rem"
                            >
                              کل: {lineTotal.toLocaleString('fa-IR')}
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

export default function DataTable({ trigger, setTotalTodaysSell }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = React.useState([]);
  const [expandedRowId, setExpandedRowId] = React.useState(null);

  const todaysList = React.useMemo(() => {
    return rows.filter(row => {
      let d = new Date();
      d.setHours(0, 0, 0, 0);
      return new Date(row.createdAt) >= d;
    });
  }, [rows]);

  const todaysStats = React.useMemo(() => {
    const overall = {
      transactionsCount: 0,
      bullion1g: 0,
      bullion2g: 0,
      bullion5g: 0,
      bullion10g: 0,
      bullion20g: 0,
      bullion50g: 0,
      bullion100g: 0,
    };

    const branchesMap = {};

    todaysList.forEach(row => {
      const branch = row.branch || 'نامشخص';
      const items = row.items || [];

      if (!branchesMap[branch]) {
        branchesMap[branch] = {
          branch: branch,
          transactionsCount: 0,
          bullion1g: 0,
          bullion2g: 0,
          bullion5g: 0,
          bullion10g: 0,
          bullion20g: 0,
          bullion50g: 0,
          bullion100g: 0,
        };
      }

      const b1 = Number(items.find(i => i.type === 'GB_999_1')?.count || 0);
      const b2 = Number(items.find(i => i.type === 'GB_999_2')?.count || 0);
      const b5 = Number(items.find(i => i.type === 'GB_999_5')?.count || 0);
      const b10 = Number(items.find(i => i.type === 'GB_999_10')?.count || 0);
      const b20 = Number(items.find(i => i.type === 'GB_999_20')?.count || 0);
      const b50 = Number(items.find(i => i.type === 'GB_999_50')?.count || 0);
      const b100 = Number(items.find(i => i.type === 'GB_999_100')?.count || 0);

      branchesMap[branch].transactionsCount += 1;
      branchesMap[branch].bullion1g += b1;
      branchesMap[branch].bullion2g += b2;
      branchesMap[branch].bullion5g += b5;
      branchesMap[branch].bullion10g += b10;
      branchesMap[branch].bullion20g += b20;
      branchesMap[branch].bullion50g += b50;
      branchesMap[branch].bullion100g += b100;

      overall.transactionsCount += 1;
      overall.bullion1g += b1;
      overall.bullion2g += b2;
      overall.bullion5g += b5;
      overall.bullion10g += b10;
      overall.bullion20g += b20;
      overall.bullion50g += b50;
      overall.bullion100g += b100;
    });

    return {
      overall,
      byBranch: Object.values(branchesMap),
    };
  }, [todaysList]);

  React.useEffect(() => {
    setTotalTodaysSell(todaysStats);
  }, [setTotalTodaysSell, todaysStats]);

  const { openDialog } = useDialogContext();

  const columns = React.useMemo(
    () => [
      // ۱. دکمه باز/بستن کشو
      {
        field: 'expand',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 44,
        renderCell: params => {
          const isExpanded = expandedRowId === params.id;
          return (
            <Tooltip title={isExpanded ? 'بستن جزئیات' : 'مشاهده اقلام و آمار'}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setExpandedRowId(prev =>
                    prev === params.id ? null : params.id
                  );
                }}
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: isExpanded ? '#ecfdf5' : 'transparent',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  '&:hover': { bgcolor: '#d1fae5' },
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    color: isExpanded ? '#059669' : '#64748b',
                    fontSize: 20,
                  }}
                />
              </IconButton>
            </Tooltip>
          );
        },
      },
      // ۲. مشتری
      {
        field: 'partyName',
        filterable: false,
        sortable: false,
        headerName: 'مشتری',
        minWidth: 140,
        flex: 0.8,
        renderCell: params => (
          <Stack spacing={0.2} justifyContent="center" height="100%">
            <Typography
              variant="body2"
              fontWeight={600}
              color="#0f172a"
              fontSize="0.82rem"
            >
              {params.value}
            </Typography>
            <Typography variant="caption" color="#64748b" fontSize="0.72rem">
              کد ملی: {params.row.partyNationalId || '—'}
            </Typography>
          </Stack>
        ),
      },
      // ۳. تاریخ
      {
        field: 'createdAt',
        sortable: false,
        filterable: false,
        headerName: 'تاریخ',
        minWidth: 120,
        flex: 0.6,
        renderCell: params => (
          <Typography variant="body2" color="text.secondary">
            {new Date(params.value).toLocaleString('fa-IR')}
          </Typography>
        ),
      },
      // ۴. شعبه
      {
        field: 'branch',
        sortable: false,
        filterable: false,
        headerName: 'شعبه',
        minWidth: 75,
        flex: 0.4,
        renderCell: params => (
          <Typography variant="body2">شعبه {params.value}</Typography>
        ),
        hide: isMobile,
      },
      // ۵. خلاصه سبد خرید
      {
        field: 'items',
        sortable: false,
        headerName: 'خلاصه سبد خرید',
        minWidth: 170,
        flex: 1,
        renderCell: params => {
          const items = params.value || [];
          const summary = items.reduce(
            (acc, curr) => {
              const weightPerItem = Number(curr.type.replace('GB_999_', ''));
              const totalWeight = curr.count * weightPerItem;
              const totalPrice = curr.count * Number(curr.price.value);
              return {
                totalWeight: acc.totalWeight + totalWeight,
                totalPrice: acc.totalPrice + totalPrice,
              };
            },
            { totalWeight: 0, totalPrice: 0 }
          );

          return (
            <Chip
              sx={{
                backgroundColor: '#ecfdf5',
                borderRadius: 2,
                height: 38,
                border: '1px solid #d1fae5',
              }}
              label={
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Stack direction="row" spacing={0.4} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      وزن:{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight={700}
                        color="#059669"
                      >
                        {Number(summary.totalWeight).toLocaleString('fa-IR')}{' '}
                        گرم
                      </Typography>
                    </Typography>
                  </Stack>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 12, my: 'auto', borderColor: '#a7f3d0' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    مبلغ:{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      fontWeight={700}
                      color="#059669"
                    >
                      {Number(summary.totalPrice).toLocaleString('fa-IR')} ریال
                    </Typography>
                  </Typography>
                </Stack>
              }
            />
          );
        },
      },
      // ۶. فاکتور
      {
        field: 'tag',
        headerName: 'فاکتور',
        filterable: false,
        sortable: false,
        minWidth: 140,
        flex: 0.7,
        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.8,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => downloadFile(params.row.tag, params.row.id)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.73rem',
                py: 0.35,
                px: 1,
                borderColor: '#059669',
                color: '#059669',
                backgroundColor: '#ffffff',
                '&:hover': {
                  borderColor: '#047857',
                  backgroundColor: '#ecfdf5',
                },
              }}
            >
              شماره{' '}
              {Number(params.row.id).toLocaleString('fa-IR', {
                useGrouping: false,
              })}
            </Button>

            <Tooltip title="تمدید فاکتور">
              <IconButton
                size="small"
                onClick={() => services.renewInvoice(params.row.id)}
                sx={{
                  color: '#059669',
                  p: 0.4,
                  '&:hover': { backgroundColor: '#ecfdf5' },
                }}
              >
                <AutorenewIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      // ۷. کد QR
      {
        field: 'qr',
        filterable: false,
        headerName: 'QR',
        sortable: false,
        minWidth: 70,
        flex: 0.35,
        renderCell: params => (
          <Button
            variant="text"
            size="small"
            startIcon={<QRIcon sx={{ fontSize: 17 }} />}
            onClick={() =>
              openDialog(
                '',
                <QRCodeDialog value={convertDataToQrCodeValue(params.row)} />
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.73rem',
              color: '#059669',
              '&:hover': { backgroundColor: '#ecfdf5' },
            }}
          >
            مشاهده
          </Button>
        ),
      },
    ],
    [isMobile, expandedRowId]
  );

  const [filterModel, setFilterModel] = React.useState({ items: [] });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await services.getInvoiceList();
        setRows(response);
      } catch (error) {
        console.error('Failed to fetch invoices:', error);
      }
    };
    fetchData();
  }, [trigger]);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid #d1fae5`,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05)',
      }}
    >
      <div
        style={{
          height: isMobile ? 'auto' : 580,
          width: '100%',
          direction: 'rtl',
          overflowX: 'auto',
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          rowHeight={isMobile ? 54 : 62}
          getRowHeight={params =>
            expandedRowId === params.id ? 'auto' : isMobile ? 54 : 62
          }
          slots={{
            row: CustomRow,
          }}
          slotProps={{
            row: {
              expandedRowId,
            },
          }}
          autoHeight={isMobile}
          filterModel={filterModel}
          onFilterModelChange={newModel => setFilterModel(newModel)}
          sx={{
            border: 'none',
            fontFamily: theme.typography.fontFamily,
            fontVariantNumeric: 'tabular-nums',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f0fdf4',
              borderBottom: `1px solid #d1fae5`,
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#065f46',
              '& .MuiDataGrid-columnHeader': {
                padding: isMobile ? '0 6px' : '0 12px',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              padding: isMobile ? '4px 6px' : '6px 12px',
              fontSize: '0.82rem',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f0fdf4',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid #d1fae5`,
              backgroundColor: '#fafcff',
            },
            '& .MuiTablePagination-root': {
              color: '#065f46',
            },
            '& .MuiTablePagination-actions button': {
              color: '#059669',
            },
          }}
        />
      </div>
    </Paper>
  );
}

export function downloadFile(address, id) {
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  fetch(`${BASE_URL}api/transactions/invoice/${address}.pdf`, {
    headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
  })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);
      anchor.href = objectUrl;
      anchor.download = `invoice-${id}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    });
}

const convertDataToQrCodeValue = data => {
  return JSON.stringify([
    data.partyPhoneNumber,
    data.partyNationalId,
    convertPersianToCustom(data.partyName),
    data.branch,
    data.createdAt,
    data.id,
    data.items.map(i => ({
      count: i.count,
      type: i.type,
      price: i.price.value,
    })),
  ]);
};

const MAP = {
  ا: 'a',
  ب: 'b',
  پ: 'c',
  ت: 'd',
  ث: 'e',
  ج: 'f',
  چ: 'g',
  ح: 'h',
  خ: 'i',
  د: 'j',
  ذ: 'k',
  ر: 'l',
  ز: 'm',
  ژ: 'n',
  س: 'o',
  ش: 'p',
  ص: 'q',
  ض: 'r',
  ط: 's',
  ظ: 't',
  ع: 'u',
  غ: 'v',
  ف: 'w',
  ق: 'x',
  ک: 'y',
  گ: 'z',
  ل: '1',
  م: '2',
  ن: '3',
  و: '4',
  ه: '5',
  ی: '6',
  ' ّ': '7',
  آ: '8',
  ء: '9',
  ' ': ' ',
  '‌': '_',
};

const convertPersianToCustom = persian => {
  return [...persian].map(p => MAP[p]).join('');
};
